import { HttpErrorResponse } from '@angular/common/http';
import {
  type MediaVisualizationBatchResult,
  type PlaybackMediaPlaylistEntity,
} from '@admefy/domain';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { WA_LOCAL_STORAGE } from '@ng-web-apis/common';
import { firstValueFrom, timeout as rxjsTimeout } from 'rxjs';
import { AppStore } from '../../+store/app.store';
import { RestService } from '../admefy-cli/admefy-cli.service';
import { PlayerInstanceIdentityService } from '../player-session/player-instance-identity.service';
import { PlayerCoordinationService } from '../playing/player-coordination.service';
import {
  IndexedDbTransactionStateUnknownError,
  IndexedDbVisualizationProofStore,
} from './indexed-db-visualization-proof.store';
import { LocalStorageVisualizationProofStore } from './local-storage-visualization-proof.store';
import { MemoryVisualizationProofStore } from './memory-visualization-proof.store';
import {
  buildVisualizationCounterKey,
  isVisualizationRecordEligibleForInstance,
  isLegacyVisualizationProofQueueItem,
  legacyQueueItemToSealedProof,
  SealedVisualizationProof,
  VisualizationCounter,
  VisualizationProofStore,
} from './visualization-proof-outbox.types';

const LEGACY_STORAGE_KEY = 'visualization-proof-queue:v1';
const BATCH_SIZE = 100;
const RETRY_INITIAL_DELAY_MS = 5000;
const RETRY_MAX_DELAY_MS = 5 * 60 * 1000;
const REQUEST_TIMEOUT_MS = 30_000;
const TERMINAL_PROOF_REASONS = new Set([
  'MEDIA_CAMPAIGN_NOT_FOUND',
  'MEDIA_CAMPAIGN_DISPLAY_MISMATCH',
  'MEDIA_VISUALIZATION_COUNT_INVALID',
  'MEDIA_VISUALIZATION_DATE_INVALID',
  'MEDIA_VISUALIZATION_PROOF_ID_REQUIRED',
  'authorization_not_found',
  'proof_identity_mismatch',
  'proof_time_invalid',
  'PROGRAMMATIC_PROOF_IDENTITY_INCOMPLETE',
  'player_session_identity_mismatch',
  'player_session_identity_incomplete',
  'playback_proof_kind_mismatch',
]);

@Injectable({
  providedIn: 'root',
})
export class VisualizationProofQueueService {
  private readonly storage = inject(WA_LOCAL_STORAGE);
  private readonly rest = inject(RestService);
  private readonly appStore = inject(AppStore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly coordination = inject(PlayerCoordinationService);
  private readonly identity = inject(PlayerInstanceIdentityService);

  private store: VisualizationProofStore | null = null;
  private storageOperations: Promise<void> = Promise.resolve();
  private readonly initialization: Promise<void>;
  private isDraining = false;
  private flushRequested = false;
  private batchEndpointUnavailable = false;
  private indexedDbStoreAttempted = false;
  private retryAttempt = 0;
  private scheduledRetry: ReturnType<typeof setTimeout> | null = null;
  private destroyed = false;

  constructor() {
    this.initialization = this.initializeStore();

    this.destroyRef.onDestroy(() => {
      this.destroyed = true;

      if (this.scheduledRetry) {
        clearTimeout(this.scheduledRetry);
        this.scheduledRetry = null;
      }

      this.store?.close();
    });
  }

  enqueueCompletedMedia(media: PlaybackMediaPlaylistEntity): void {
    if (!media.id || !media.identifier || this.destroyed) {
      return;
    }

    const counter = this.buildCounter(media);
    if (!counter) return;

    void this.runStorageOperation((store) => store.incrementCounter(counter))
      .then(() => {
        if (this.appStore.isOnline() && this.appStore.token()) {
          this.flush();
        }
      })
      .catch((error) => {
        console.warn('Could not persist media visualization proof', error);
      });
  }

  async prepareForReload(signal?: AbortSignal): Promise<boolean> {
    // Queue behind pending IndexedDB/localStorage writes. Memory-only proofs must
    // survive in this document, so postpone automatic updates in that case.
    const canLeaveOutbox = () => this.runStorageOperation(async (store) => {
      if (store.kind !== 'memory') return true;
      const state = await store.exportState();
      if (!state.counters.length && !state.proofs.length) return true;
      const recovered = this.createHealthyLocalStorageStore();
      if (!recovered) return false;
      try {
        await recovered.importState(state);
        this.store = recovered;
        store.close();
        return true;
      } catch {
        recovered.close();
        return false;
      }
    }).catch(() => false);
    // A completion may still be draining when its media ends. Recheck within
    // the updater's bounded hold instead of rejecting every boundary forever.
    for (let attempt = 0; !this.destroyed && !signal?.aborted; attempt++) {
      if (await canLeaveOutbox() && !this.coordination.hasPendingCompletions()) return true;
      if (!signal && attempt >= 20) return false;
      this.flush();
      await new Promise(resolve => setTimeout(resolve, 50));
    }
    return false;
  }

  flush(): void {
    if (this.destroyed || !this.appStore.isOnline() || !this.appStore.token()) {
      return;
    }

    if (this.isDraining) {
      this.flushRequested = true;
      return;
    }

    if (this.scheduledRetry) {
      return;
    }

    this.isDraining = true;
    this.flushRequested = false;

    void this.drainQueue()
      .catch((error) => {
        if (!this.destroyed) {
          console.warn('Could not drain media visualization proofs', error);
          this.scheduleRetry();
        }
      })
      .finally(() => {
        this.isDraining = false;

        if (this.flushRequested && !this.scheduledRetry && !this.destroyed) {
          this.flushRequested = false;
          this.flush();
        }
      });
  }

  private buildCounter(media: PlaybackMediaPlaylistEntity): VisualizationCounter | null {
    const now = new Date();
    const date = now.toISOString();
    const aggregationDate = `${date.slice(0, 10)}T00:00:00.000Z`;
    const proofContext = this.coordination.getProofContext(media.identifier);
    if (media.slotType === 'programmatic') {
      if (!media.programmaticDeliveryId || !proofContext?.authorizationId) return null;
      const counter: VisualizationCounter = {
        key: '',
        kind: 'programmatic',
        programmaticDeliveryId: media.programmaticDeliveryId,
        authorizationId: proofContext.authorizationId,
        mediaId: media.id,
        identifier: media.identifier,
        ...proofContext,
        date,
        visualizations: 1,
        updatedAt: now.getTime(),
      };
      counter.key = buildVisualizationCounterKey({ ...counter, date: aggregationDate });
      return counter;
    }
    if (!media.mediaCampaignId) return null;
    const counter = {
      key: '',
      kind: 'campaign' as const,
      mediaCampaignId: media.mediaCampaignId,
      mediaId: media.id,
      identifier: media.identifier,
      ...proofContext,
      date,
      visualizations: 1,
      updatedAt: now.getTime(),
    };

    counter.key = buildVisualizationCounterKey({ ...counter, date: aggregationDate });
    return counter;
  }

  private async drainQueue(): Promise<void> {
    await Promise.all([this.initialization, this.identity.ready]);
    const instanceId = this.identity.value;

    while (!this.destroyed && this.appStore.isOnline() && this.appStore.token()) {
      let proofs = await this.runStorageOperation((store) =>
        store.listProofs(BATCH_SIZE, instanceId),
      );

      if (proofs.length < BATCH_SIZE) {
        await this.runStorageOperation((store) =>
          store.sealCounters(BATCH_SIZE - proofs.length, instanceId),
        );
        proofs = await this.runStorageOperation((store) =>
          store.listProofs(BATCH_SIZE, instanceId),
        );
      }

      if (proofs.length === 0) {
        if (await this.activateIndexedDbAfterLocalStorageDrain()) {
          continue;
        }

        this.retryAttempt = 0;
        return;
      }

      try {
        const fullyAcknowledged = this.batchEndpointUnavailable
          ? await this.sendIndividually(proofs)
          : await this.sendBatch(proofs);

        if (!fullyAcknowledged) {
          this.scheduleRetry();
          return;
        }

        this.retryAttempt = 0;
      } catch (error) {
        if (!this.batchEndpointUnavailable && this.isBatchEndpointUnavailable(error)) {
          this.batchEndpointUnavailable = true;
          continue;
        }

        this.scheduleRetry();
        return;
      }
    }
  }

  private async sendBatch(proofs: SealedVisualizationProof[]): Promise<boolean> {
    const response = await firstValueFrom(
      this.rest
        .post('/media-visualization-data/batch', {
          items: proofs.map((proof) => ({
            kind: proof.kind,
            mediaCampaignId: proof.mediaCampaignId,
            programmaticDeliveryId: proof.programmaticDeliveryId,
            mediaId: proof.mediaId,
            authorizationId: proof.authorizationId,
            proofId: proof.proofId,
            instanceId: proof.instanceId,
            occurrenceId: proof.occurrenceId,
            playerProtocolVersion: proof.playerProtocolVersion,
            date: proof.date,
            visualizations: proof.visualizations,
          })),
        })
        .pipe(rxjsTimeout({ first: REQUEST_TIMEOUT_MS })),
    );
    const result = response.body?.data as MediaVisualizationBatchResult | undefined;

    if (!isMediaVisualizationBatchResult(result)) {
      throw new Error('Invalid media visualization batch response');
    }

    const submittedProofIds = new Set(proofs.map((proof) => proof.proofId));
    const terminalRejectedProofs = result.rejectedProofs.filter(
      (proof) => submittedProofIds.has(proof.proofId) && TERMINAL_PROOF_REASONS.has(proof.reason),
    );
    const acknowledgedProofIds = [
      ...result.acceptedProofIds,
      ...terminalRejectedProofs.map((proof) => proof.proofId),
    ].filter((proofId) => submittedProofIds.has(proofId));

    for (const rejectedProof of terminalRejectedProofs) {
      console.warn(
        `Discarding terminal media visualization proof ${rejectedProof.proofId}: ${rejectedProof.reason}`,
      );
    }

    await this.runStorageOperation((store) => store.deleteProofs(acknowledgedProofIds));

    return new Set(acknowledgedProofIds).size === submittedProofIds.size;
  }

  private async sendIndividually(proofs: SealedVisualizationProof[]): Promise<boolean> {
    for (const proof of proofs) {
      if (this.destroyed || !this.appStore.isOnline() || !this.appStore.token()) {
        return false;
      }

      if (proof.kind === 'programmatic') return false;

      try {
        await firstValueFrom(
          this.rest
            .post('/media-visualization-data', {
              mediaCampaignId: proof.mediaCampaignId,
              proofId: proof.proofId,
              instanceId: proof.instanceId,
              occurrenceId: proof.occurrenceId,
              playerProtocolVersion: proof.playerProtocolVersion,
              date: proof.date,
              visualizations: proof.visualizations,
            })
            .pipe(rxjsTimeout({ first: REQUEST_TIMEOUT_MS })),
        );
        await this.runStorageOperation((store) => store.deleteProofs([proof.proofId]));
      } catch (error) {
        if (this.isTerminalError(error)) {
          await this.runStorageOperation((store) => store.deleteProofs([proof.proofId]));
          continue;
        }

        throw error;
      }
    }

    return true;
  }

  private scheduleRetry(): void {
    if (this.scheduledRetry || this.destroyed) {
      return;
    }

    const exponentialDelay = Math.min(
      RETRY_INITIAL_DELAY_MS * 2 ** this.retryAttempt,
      RETRY_MAX_DELAY_MS,
    );
    const jitter = 0.8 + Math.random() * 0.4;
    const retryDelay = Math.round(exponentialDelay * jitter);
    this.retryAttempt += 1;

    this.scheduledRetry = setTimeout(() => {
      this.scheduledRetry = null;
      this.flush();
    }, retryDelay);
  }

  private isTerminalError(error: unknown): boolean {
    if (!(error instanceof HttpErrorResponse)) {
      return false;
    }

    return TERMINAL_PROOF_REASONS.has(extractErrorReason(error.error));
  }

  private isBatchEndpointUnavailable(error: unknown): boolean {
    return error instanceof HttpErrorResponse && (error.status === 404 || error.status === 405);
  }

  private runStorageOperation<T>(
    operation: (store: VisualizationProofStore) => Promise<T>,
  ): Promise<T> {
    const result = this.storageOperations.then(async () => {
      await this.initialization;

      if (this.destroyed) {
        throw new Error('Visualization proof queue was destroyed');
      }

      if (!this.store) {
        throw new Error('Visualization proof store is not initialized');
      }

      try {
        return await operation(this.store);
      } catch (error) {
        if (this.destroyed) {
          throw error;
        }

        await this.degradeStore(error);

        if (error instanceof IndexedDbTransactionStateUnknownError) {
          throw error;
        }

        if (!this.store) {
          throw error;
        }

        return operation(this.store);
      }
    });

    this.storageOperations = result.then(
      () => undefined,
      () => undefined,
    );

    return result;
  }

  private async initializeStore(): Promise<void> {
    await this.identity.ready;
    const instanceId = this.identity.value;
    let localStore = this.createHealthyLocalStorageStore();

    if (localStore) {
      try {
        const localState = await localStore.exportState();
        if (hasEligibleOutboxRecords(localState, instanceId)) {
          this.store = localStore;

          try {
            await this.migrateLegacyQueue(this.store);
          } catch (error) {
            await this.degradeStore(error);

            if (!this.store) {
              throw error;
            }

            await this.migrateLegacyQueue(this.store);
          }

          this.closeStoreIfDestroyed();
          return;
        }
      } catch (error) {
        localStore.close();
        localStore = null;
        console.warn('Shared localStorage unavailable for visualization proofs', error);
      }
    }

    const indexedDb = this.getIndexedDb();

    if (indexedDb) {
      this.indexedDbStoreAttempted = true;
      try {
        this.store = await IndexedDbVisualizationProofStore.open(indexedDb);
        await this.migrateLegacyQueue(this.store);
        this.closeStoreIfDestroyed();
        return;
      } catch (error) {
        this.store?.close();
        this.store = null;
        console.warn('IndexedDB unavailable for visualization proofs', error);
      }
    }

    this.store = localStore ?? new MemoryVisualizationProofStore();

    try {
      await this.migrateLegacyQueue(this.store);
    } catch (error) {
      await this.degradeStore(error);

      if (!this.store) {
        throw error;
      }

      await this.migrateLegacyQueue(this.store);
    }

    this.closeStoreIfDestroyed();
  }

  private async activateIndexedDbAfterLocalStorageDrain(): Promise<boolean> {
    if (this.indexedDbStoreAttempted || this.store?.kind !== 'local-storage') {
      return false;
    }

    await this.identity.ready;
    const instanceId = this.identity.value;

    return this.runStorageOperation(async (localStore) => {
      if (this.indexedDbStoreAttempted || localStore.kind !== 'local-storage') {
        return false;
      }

      const localState = await localStore.exportState();
      if (hasEligibleOutboxRecords(localState, instanceId)) {
        return false;
      }

      const indexedDb = this.getIndexedDb();
      this.indexedDbStoreAttempted = true;
      if (!indexedDb) {
        return false;
      }

      try {
        const indexedDbStore = await IndexedDbVisualizationProofStore.open(indexedDb);
        this.store = indexedDbStore;
        localStore.close();
        return true;
      } catch (error) {
        console.warn('IndexedDB unavailable for visualization proofs', error);
        return false;
      }
    });
  }

  private async degradeStore(cause: unknown): Promise<void> {
    const failingStore = this.store;

    if (!failingStore || failingStore.kind === 'memory') {
      throw cause;
    }

    let fallback: VisualizationProofStore;

    if (failingStore.kind === 'indexed-db') {
      fallback = this.createHealthyLocalStorageStore() ?? new MemoryVisualizationProofStore();
    } else {
      fallback = new MemoryVisualizationProofStore();
    }

    try {
      await this.transferRecoverableState(failingStore, fallback);
      this.store = fallback;
    } catch (transferError) {
      fallback.close();
      const memoryStore = new MemoryVisualizationProofStore();
      await this.transferRecoverableState(failingStore, memoryStore).catch(() => undefined);
      this.store = memoryStore;

      console.warn('Could not fully transfer media visualization outbox state', transferError);
    }

    failingStore.close();

    console.warn(
      `Visualization proof storage degraded from ${failingStore.kind} to ${this.store.kind}`,
      cause,
    );
  }

  private async transferRecoverableState(
    source: VisualizationProofStore,
    target: VisualizationProofStore,
  ): Promise<void> {
    await this.identity.ready;
    const instanceId = this.identity.value;
    let canSealCounters = true;

    while (!this.destroyed) {
      let proofs = await source.listProofs(BATCH_SIZE, instanceId);

      if (canSealCounters && proofs.length < BATCH_SIZE) {
        try {
          await source.sealCounters(BATCH_SIZE - proofs.length, instanceId);
          proofs = await source.listProofs(BATCH_SIZE, instanceId);
        } catch {
          // Existing sealed proofs still have stable ids and are safe to recover. Counters stay
          // persisted in the failing source until it becomes writable again.
          canSealCounters = false;
        }
      }

      if (proofs.length === 0) {
        return;
      }

      await target.importState({ counters: [], proofs });

      if (target.kind === 'memory') {
        // Memory is bounded and cannot acknowledge durable ownership of an arbitrary backlog.
        // Keep the originals for a later persistent-store recovery; proof ids make replays safe.
        return;
      }

      try {
        await source.deleteProofs(proofs.map((proof) => proof.proofId));
      } catch {
        // The duplicate source copy is safe because proof ids are idempotent. Stop to avoid
        // repeatedly importing the same batch while the source remains read-only.
        return;
      }
    }
  }

  private createHealthyLocalStorageStore(): LocalStorageVisualizationProofStore | null {
    if (!this.storage) {
      return null;
    }

    try {
      const store = LocalStorageVisualizationProofStore.openShared(this.storage);
      if (!store) {
        return null;
      }
      store.probe();
      return store;
    } catch {
      return null;
    }
  }

  private getIndexedDb(): IDBFactory | null {
    try {
      return globalThis.indexedDB ?? null;
    } catch {
      return null;
    }
  }

  private async migrateLegacyQueue(store: VisualizationProofStore): Promise<void> {
    if (!this.storage) {
      return;
    }

    let rawValue: string | null;

    try {
      rawValue = this.storage.getItem(LEGACY_STORAGE_KEY);
    } catch {
      return;
    }

    if (!rawValue) {
      return;
    }

    let parsedValue: unknown;

    try {
      parsedValue = JSON.parse(rawValue);
    } catch {
      return;
    }

    if (!Array.isArray(parsedValue)) {
      return;
    }

    const proofs = parsedValue
      .filter(isLegacyVisualizationProofQueueItem)
      .map(legacyQueueItemToSealedProof);

    await store.importState({
      counters: [],
      proofs,
    });

    try {
      this.storage.removeItem(LEGACY_STORAGE_KEY);
    } catch {
      // The imported proof ids make this migration repeat-safe on the next startup.
    }
  }

  private closeStoreIfDestroyed(): void {
    if (this.destroyed) {
      this.store?.close();
    }
  }
}

function isMediaVisualizationBatchResult(value: unknown): value is MediaVisualizationBatchResult {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const result = value as Partial<MediaVisualizationBatchResult>;

  return Array.isArray(result.acceptedProofIds) && Array.isArray(result.rejectedProofs);
}

function hasEligibleOutboxRecords(
  state: { counters: VisualizationCounter[]; proofs: SealedVisualizationProof[] },
  instanceId: string,
): boolean {
  return (
    state.counters.some((counter) =>
      isVisualizationRecordEligibleForInstance(counter, instanceId),
    ) ||
    state.proofs.some((proof) => isVisualizationRecordEligibleForInstance(proof, instanceId))
  );
}

function extractErrorReason(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }

  if (!value || typeof value !== 'object') {
    return '';
  }

  const error = value as Record<string, unknown>;

  for (const key of ['code', 'message', 'error', 'reason']) {
    const reason = extractErrorReason(error[key]);
    if (reason) {
      return reason;
    }
  }

  return '';
}
